import React from "react";
import {Navigate, Outlet, Route, createBrowserRouter, createRoutesFromElements} from "react-router-dom";
import {CourseLibraryPage as CourseLibraryPageV2} from "@/app2/pages/CourseLibraryPage/CourseLibraryPage";
import {CourseLibraryProductPage as CourseLibraryProductPageV2} from "@/app2/pages/CourseLibraryProductPage/CourseLibraryProductPage";
import {CoursePage as CoursePageV2} from "./app2/pages/CoursePage/CoursePage";
import {MyProgressPage as MyProgressPageV2} from "./app2/pages/MyProgressPage";
import {RequireAuth} from "./routes/RequireAuth";
import {RequireAdmin} from "./routes/RequireAdmin";
import {ContentManager as ContentManagerV2} from "./app2/pages/ManagerView/ContentManager";
import {FeedLandingPage as FeedLandingPageV2} from "./app2/pages/TrainingFeedPage/FeedLandingPage";
import AppUrlListener from "./routes/AppUrlListener";
import {MyNotesPage as MyNotesPageV2} from "./app2/pages/MyNotesPage";
import {RootLayout} from "./RootLayout";
import useSnackbarManager from "./snackbars/useSnackbarManager";
import {HomePage as HomePageV2} from "@/app2/pages/HomePage/HomePage";
import {QueryClient} from "@tanstack/react-query";
import {WebSocketProvider} from "./app2/shared/contexts/WebSocketContext";
import {AccessRestrictedProvider, EnsureAccountProvider} from "@/app2/shared/contexts";
import {BulkActionSnackbarProvider} from "@/app2/shared/contexts/BulkActionSnackbarContext";
import {LocaleProvider} from "./app2/shared/contexts";
import {useAccessToken} from "./app2/shared/hooks/useAccessToken";
import {useApp2SnackbarManager} from "./app2/shared/contexts/useApp2SnackbarManager";
import {App2NotificationProvider} from "./app2/shared/contexts/App2NotificationProvider";
import {SettingsPage as SettingsPageV2} from "@/app2/pages/Settings/Settings";
import {NotificationDrawerProvider} from "@/app2/shared/contexts/NotificationDrawerContext";
import {PointsEarnedFloaterProvider} from "@/app2/shared/contexts/PointsEarnedFloaterContext";
import {RxdLayout} from "./RxdLayout";
import {RxdCoursePage} from "./pages/RxdCoursePage";
import {LessonDetail} from "./pages/RxdCoursePage/LessonDetail";
import {LoginPage} from "./pages/LoginPage/LoginPage";
import {AnnouncementLandingPage} from "./pages/ModulePage/AnnouncementPage/AnnouncementLandingPage";
import {NotFoundPage} from "./pages/NotFoundPage/NotFoundPage";
import useDatadog from "./hooks/useDatadog";
import {useDevCycleIdentifier} from "./hooks/useDevCycleSdk";

const App2Routes = () => {
	return (
		createRoutesFromElements(
			<>
				{/* RXD routes - standalone without RootLayout */}
				<Route path="/rxd/:courseSlug/*" element={
					<RxdLayout>
						<RxdCoursePage />
					</RxdLayout>
				} />

				<Route path="/module/:id" element={<RxdLayout>
					<LessonDetail />
				</RxdLayout>} />

				<Route element={<RootLayout />}>
					<Route index element={<LoginPage />} />
					<Route path="/callback" element={<LoginPage />} />
					<Route path="/training-feed" element={<RequireAuth><Navigate to="/home" replace /></RequireAuth>} />
					<Route path="/home" element={<RequireAuth><HomePageV2 /></RequireAuth>} />
					<Route path="/my-progress" element={<RequireAuth><MyProgressPageV2 /></RequireAuth>} />
					<Route path="/notes" element={<RequireAuth><MyNotesPageV2 /></RequireAuth>} />
					<Route path="/admin/" element={<Navigate to="/admin/users" replace />} />
					<Route path="/admin/*" element={<RequireAuth><RequireAdmin><ContentManagerV2 /></RequireAdmin></RequireAuth>} />
					<Route path="/course-library" element={<RequireAuth><CourseLibraryPageV2 /></RequireAuth>} />
					<Route path="/course-library/:courseSlug/*" element={<RequireAuth><CourseLibraryProductPageV2 /></RequireAuth>} />
					<Route path="/course/:courseSlug/*" element={<RequireAuth><CoursePageV2 /></RequireAuth>} />
					<Route path="/feed/announcement/:slug/*" element={<RequireAuth><AnnouncementLandingPage /></RequireAuth>} />
					<Route path="/feed/:courseSlug/*" element={<RequireAuth><CoursePageV2 /></RequireAuth>} />
					<Route path="/feed" element={<RequireAuth><FeedLandingPageV2 /></RequireAuth>} />
					<Route path="/settings" element={<RequireAuth><SettingsPageV2 /></RequireAuth>} />
					<Route path="*" element={<NotFoundPage />} />
				</Route>
			</>
		)
	)
}


export const createRouter = () => createBrowserRouter(
	App2Routes()
);

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false
		}
	}
});

const LegacyApp: React.FC = () => {
	useSnackbarManager();
	return (
		<main style={{height: "100%"}}>
			<AppUrlListener></AppUrlListener>
			<div className="App">
				<Outlet />
			</div>
		</main>
	);
};

/**
 * Account bootstrap, bulk-action UI, WebSocket, and notification drawer.
 * Only mounted once {@link useAccessToken} is set so learner/admin API hooks in those trees are not instantiated on public routes (e.g. `/`).
 */
const App2AuthenticatedProviders: React.FC<{children: React.ReactElement}> = ({children}) => (
	<AccessRestrictedProvider>
		<EnsureAccountProvider>
			<PointsEarnedFloaterProvider>
				<BulkActionSnackbarProvider>
					<WebSocketProvider>
						<NotificationDrawerProvider>{children}</NotificationDrawerProvider>
					</WebSocketProvider>
				</BulkActionSnackbarProvider>
			</PointsEarnedFloaterProvider>
		</EnsureAccountProvider>
	</AccessRestrictedProvider>
);

const App2Content: React.FC = () => {
	useApp2SnackbarManager();
	const accessToken = useAccessToken();
	const appShell = (
		<main style={{height: "100%"}}>
			<AppUrlListener></AppUrlListener>
			<div className="App">
				<Outlet />
			</div>
		</main>
	);
	return (
		<LocaleProvider>
			{accessToken ? (
				<App2AuthenticatedProviders>{appShell}</App2AuthenticatedProviders>
			) : (
				appShell
			)}
		</LocaleProvider>
	);
};

const App2: React.FC = () => {
	return (
		<App2NotificationProvider>
			<App2Content />
		</App2NotificationProvider>
	);
};

const Main: React.FC = () => {
	useDatadog();
	useDevCycleIdentifier();
	return <App2 />;
};

export default Main;